import React from 'react';
import {
  FaTag,
  FaBarcode,
  FaLink,
  FaUnlink,
  FaSearch,
  FaEdit
} from 'react-icons/fa';
import { getMappingStatusBadge } from './statusBadges';

/**
 * Sortable, selectable table of invoice line items.
 */
const InvoiceItemsTable = ({
  invoice,
  sortConfig,
  handleSort,
  getSortedItems,
  selectedItems,
  handleItemSelection,
  handleSelectAllItems,
  openMappingModal,
  openEditModal,
  handleQuickNameUpdate,
  editLocked = false
}) => {
  const items = invoice?.items || [];

  return (
    <div style={{
      backgroundColor: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: '0.375rem',
      boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
      marginTop: '1rem'
    }}>
      {/* Section header */}
      <div style={{
        padding: '0.75rem 1rem',
        borderBottom: '1px solid var(--border)',
        backgroundColor: 'var(--bg)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h6 style={{ margin: 0, color: 'var(--text-secondary)', fontWeight: '600', fontSize: '0.9rem' }}>
          Pozycje faktury ({items.length})
        </h6>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={handleSelectAllItems}
            style={{
              padding: '6px 12px', fontSize: '12px',
              border: '1px solid var(--info)',
              backgroundColor: 'var(--card-bg)', color: 'var(--info)',
              cursor: 'pointer', fontWeight: '500'
            }}
          >
            {selectedItems.length === items.length ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
          </button>
          <button
            disabled={selectedItems.length === 0}
            style={{
              padding: '6px 12px', fontSize: '12px',
              border: '1px solid var(--warning)',
              backgroundColor: selectedItems.length === 0 ? 'var(--border)' : 'var(--warning)',
              color: selectedItems.length === 0 ? 'var(--text-muted)' : '#000',
              cursor: selectedItems.length === 0 ? 'not-allowed' : 'pointer',
              fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.25rem'
            }}
          >
            <FaLink />
            Mapuj zaznaczone ({selectedItems.length})
          </button>
        </div>
      </div>

      {/* Table body */}
      <div style={{ padding: '0' }}>
        {items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-muted)' }}>
            <FaTag style={{ fontSize: '3rem', marginBottom: '1rem', color: 'var(--border)' }} />
            <p style={{ margin: 0, fontSize: '0.875rem' }}>Brak pozycji na fakturze</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead style={{ backgroundColor: 'var(--text)', color: '#fff' }}>
                <tr>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'left', width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={items.length > 0 && selectedItems.length === items.length}
                      onChange={handleSelectAllItems}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'left' }}>Lp</th>
                  <th
                    style={{ padding: '0.75rem 0.5rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('nazwa_produktu')}
                    title="Kliknij aby posortowac alfabetycznie"
                  >
                    Nazwa produktu {sortConfig.key === 'nazwa_produktu' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                  </th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'left' }}>Kod / EAN</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'left' }}>J.m.</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Ilosc</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Cena netto</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>VAT%</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Wartosc brutto</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>Status mapowania</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'left' }}>Mapowany produkt</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {getSortedItems().map((item, index) => (
                  <ItemRow
                    key={`item-${item.id}-${index}`}
                    item={item}
                    index={index}
                    selected={selectedItems.includes(item.id)}
                    onSelect={handleItemSelection}
                    onMap={openMappingModal}
                    onEdit={openEditModal}
                    onQuickNameUpdate={handleQuickNameUpdate}
                    editLocked={editLocked}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/* ---- Row sub-component ---- */

const ItemRow = ({ item, index, selected, onSelect, onMap, onEdit, onQuickNameUpdate, editLocked = false }) => (
  <tr style={{
    backgroundColor: selected ? 'var(--info-light)' : (index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)'),
    borderBottom: '1px solid var(--border)'
  }}>
    <td style={{ padding: '0.75rem 0.5rem' }}>
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onSelect(item.id)}
        style={{ cursor: 'pointer' }}
      />
    </td>
    <td style={{ padding: '0.75rem 0.5rem' }}>{item.lp}</td>
    {/* Product name + name-diff warning */}
    <td style={{ padding: '0.75rem 0.5rem' }}>
      <div>
        <div style={{ fontWeight: '600' }}>{item.nazwa_produktu}</div>
        {item.opis && (
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
            {item.opis}
          </div>
        )}
        {item.produkt_id && item.produkt_nazwa_baza && item.nazwa_produktu !== item.produkt_nazwa_baza && (
          <div style={{
            marginTop: '0.5rem', padding: '0.5rem', backgroundColor: 'var(--warning-light)',
            borderRadius: '0.25rem', fontSize: '0.75rem', border: '1px solid var(--warning)'
          }}>
            <div style={{ color: 'var(--warning)', fontWeight: '600', marginBottom: '0.25rem' }}>
              Roznica w nazwie:
            </div>
            <div style={{ color: 'var(--text-muted)' }}>
              <strong>W bazie:</strong> {item.produkt_nazwa_baza}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
              <button
                onClick={() => onQuickNameUpdate(item.produkt_id, item.nazwa_produktu)}
                style={{
                  padding: '4px 10px', border: '1px solid var(--success)', color: 'var(--success)' ,
                  cursor: 'pointer',
                  fontSize: '11px', fontWeight: '600'
                }}
              >
                Aktualizuj nazwe w bazie
              </button>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                (zmieni na: {item.nazwa_produktu.substring(0, 40)}...)
              </span>
            </div>
          </div>
        )}
      </div>
    </td>
    {/* Codes */}
    <td style={{ padding: '0.75rem 0.5rem' }}>
      <div>
        {item.kod_produktu && (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
            <FaTag style={{ marginRight: '0.25rem', fontSize: '0.75rem' }} />
            {item.kod_produktu}
          </div>
        )}
        {item.ean && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FaBarcode style={{ marginRight: '0.25rem', fontSize: '0.75rem' }} />
            {item.ean}
          </div>
        )}
      </div>
    </td>
    <td style={{ padding: '0.75rem 0.5rem' }}>{item.jednostka}</td>
    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{item.ilosc}</td>
    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>
      {item.cena_netto ? `${parseFloat(item.cena_netto).toFixed(2)} zl` : '-'}
    </td>
    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>{item.stawka_vat}%</td>
    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', fontWeight: '600' }}>
      {item.wartosc_brutto ? `${parseFloat(item.wartosc_brutto).toFixed(2)} zl` : '-'}
    </td>
    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>
      {getMappingStatusBadge(item.status_mapowania)}
    </td>
    {/* Mapped product */}
    <td style={{ padding: '0.75rem 0.5rem' }}>
      {(item.produkt_id || item.mapped_product_id) ? (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FaLink style={{ color: 'var(--success)', marginRight: '0.5rem' }} />
          <div>
            <div style={{ fontSize: '0.8rem' }}>{item.produkt_nazwa || item.produkt_nazwa_baza}</div>
            {item.mapping_score && (
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                Podobienstwo: {Math.round(item.mapping_score * 100)}%
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', color: 'var(--text-muted)' }}>
          <FaUnlink style={{ marginRight: '0.5rem' }} />
          Niezmapowany
        </div>
      )}
    </td>
    {/* Actions */}
    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>
      <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
        <button
          onClick={() => onMap(item)}
          title="Mapuj produkt"
          style={{
            padding: '4px 10px', border: '1px solid var(--info)',
            backgroundColor: 'transparent',
            color: 'var(--info)', cursor: 'pointer', fontSize: '11px'
          }}
        >
          <FaSearch />
        </button>
        <button
          onClick={() => onEdit(item)}
          disabled={editLocked}
          title={editLocked ? 'Faktura ma PZ — edycja tylko przez korektę' : 'Edytuj pozycje'}
          style={{
            padding: '4px 10px',
            border: `1px solid ${editLocked ? 'var(--border)' : 'var(--warning)'}`,
            backgroundColor: 'transparent',
            color: editLocked ? 'var(--text-muted)' : 'var(--warning)',
            cursor: editLocked ? 'not-allowed' : 'pointer', fontSize: '11px'
          }}
        >
          <FaEdit />
        </button>
      </div>
    </td>
  </tr>
);

export default InvoiceItemsTable;
